import * as Yup from 'yup';
import { useMemo, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ----------------------------------------------------------------------

export function useDynamicForm({
    fields = [],
    validationSchema,
    defaultValues,
    currentData = null,
    showAvatar = false,
    siteSettings = null,
}) {
    // Build validation schema if not provided
    const schema = useMemo(() => {
        if (validationSchema) return validationSchema;

        // Helper to extract a primitive value from potentially complex types (objects from Autocomplete)
        const getPrimitiveValue = (val) => {
            if (val === null || val === undefined) return val;

            // Handle arrays (e.g. from multi-select or sometimes single-select wrappers)
            if (Array.isArray(val)) {
                if (val.length === 0) return null;
                // For visibility rules, we usually compare against the first selected value if it's treated as a single value
                return getPrimitiveValue(val[0]);
            }

            if (typeof val === 'object') {
                // Return value, id, _id, or the object itself if no standard key is found
                let innerVal = val.value !== undefined ? val.value : (val.id ?? val._id ?? val);
                // Recursively handle if the inner value is still an object or array
                if (typeof innerVal === 'object' && innerVal !== val) {
                    innerVal = getPrimitiveValue(innerVal);
                }
                return innerVal;
            }
            return val;
        };

        const evaluateRule = (rule, watchedValue) => {
            const { operator, value: compareVal } = rule;

            const actualValue = getPrimitiveValue(watchedValue);
            const actualCompareVal = getPrimitiveValue(compareVal);

            // Normalize values for comparison (trim strings)
            const normalize = (v) => (typeof v === 'string' ? v.trim() : v);
            const normalizedValue = normalize(actualValue);
            const normalizedCompareVal = normalize(actualCompareVal);

            // Handle array watchedValue (e.g. checkbox-group, multi-select)
            if (Array.isArray(watchedValue)) {
                const unwrappedArray = watchedValue.map(getPrimitiveValue);
                switch (operator) {
                    case 'equals':
                        return unwrappedArray.some(item => {
                            const nItem = normalize(item);
                            const nComp = normalize(actualCompareVal);
                            if (typeof nItem === 'string' && typeof nComp === 'string') {
                                return nItem.toLowerCase() === nComp.toLowerCase();
                            }
                            // eslint-disable-next-line eqeqeq
                            return nItem == nComp;
                        });
                    case 'not_equals':
                        return !unwrappedArray.some(item => {
                            const nItem = normalize(item);
                            const nComp = normalize(actualCompareVal);
                            if (typeof nItem === 'string' && typeof nComp === 'string') {
                                return nItem.toLowerCase() === nComp.toLowerCase();
                            }
                            // eslint-disable-next-line eqeqeq
                            return nItem == nComp;
                        });
                    case 'contains':
                        return unwrappedArray.some(item =>
                            String(item || '').toLowerCase().includes(String(actualCompareVal || '').toLowerCase())
                        );
                    case 'is_filled':
                    case 'is_not_empty':
                        return unwrappedArray.length > 0;
                    case 'is_empty':
                        return unwrappedArray.length === 0;
                    default:
                        break;
                }
            }

            switch (operator) {
                case 'equals':
                    // eslint-disable-next-line eqeqeq
                    if (typeof normalizedValue === 'string' && typeof normalizedCompareVal === 'string') {
                        return normalizedValue.toLowerCase() === normalizedCompareVal.toLowerCase();
                    }
                    // eslint-disable-next-line eqeqeq
                    return normalizedValue == normalizedCompareVal;

                case 'not_equals':
                    // eslint-disable-next-line eqeqeq
                    if (typeof normalizedValue === 'string' && typeof normalizedCompareVal === 'string') {
                        return normalizedValue.toLowerCase() !== normalizedCompareVal.toLowerCase();
                    }
                    // eslint-disable-next-line eqeqeq
                    return normalizedValue != normalizedCompareVal;

                case 'contains':
                    return String(normalizedValue || '').toLowerCase().includes(String(normalizedCompareVal || '').toLowerCase());

                case 'greater_than':
                    return Number(normalizedValue) > Number(normalizedCompareVal);

                case 'less_than':
                    return Number(normalizedValue) < Number(normalizedCompareVal);

                case 'is_filled':
                case 'is_not_empty':
                    return actualValue !== null && actualValue !== '' && actualValue !== undefined && (Array.isArray(watchedValue) ? watchedValue.length > 0 : true);

                case 'is_empty':
                    return actualValue === null || actualValue === '' || actualValue === undefined || (Array.isArray(watchedValue) && watchedValue.length === 0);

                default:
                    return true;
            }
        };

        // Helper to check if a field is visible based on form data (using Yup's .when() context)
        const checkFieldVisibility = (field, currentFormData) => {
            // Helper to get value from formData even if path is nested (e.g. "address.city")
            const getFieldValue = (path) => {
                if (!path) return undefined;
                return path.split('.').reduce((acc, part) => acc?.[part], currentFormData);
            };

            // 0. Legacy / Function-based Hidden Check
            if (typeof field.hidden === 'function') {
                if (field.hidden(currentFormData, userRole)) return false;
            }

            // 1. Advanced Visibility Group (New Format)
            if (field.visibilityGroup && field.visibilityGroup.rules && field.visibilityGroup.rules.length > 0) {
                const { match, rules } = field.visibilityGroup;
                const results = rules.map(rule => {
                    if (!rule.field) return true;
                    const watchedValue = getFieldValue(rule.field);
                    return evaluateRule(rule, watchedValue);
                });

                if (String(match).toUpperCase() === 'OR') return results.some(res => res === true);
                return results.every(res => res === true);
            }

            // 2. Simple JSON Rules Check (Builder generated fallback)
            if (field.visibilityRules && field.visibilityRules.field) {
                const watchedValue = getFieldValue(field.visibilityRules.field);
                return evaluateRule(field.visibilityRules, watchedValue);
            }

            return true;
        };

        // Helper to apply conditional logic to schema (supports inheriting section visibility)
        const applyVisibilityRules = (baseSchema, field, enclosingSectionField = null) => {
            // Collect all fields that this field depends on for visibility
            const dependentFields = [];
            if (field.conditional?.field) {
                dependentFields.push(field.conditional.field);
            }
            if (field.visibilityGroup && field.visibilityGroup.rules) {
                field.visibilityGroup.rules.forEach(r => { if (r.field && !dependentFields.includes(r.field)) dependentFields.push(r.field); });
            } else if (field.visibilityRules && field.visibilityRules.field) {
                if (!dependentFields.includes(field.visibilityRules.field)) dependentFields.push(field.visibilityRules.field);
            }

            // Also inherit dependencies from enclosing section_break
            if (enclosingSectionField) {
                if (enclosingSectionField.visibilityGroup && enclosingSectionField.visibilityGroup.rules) {
                    enclosingSectionField.visibilityGroup.rules.forEach(r => {
                        if (r.field && !dependentFields.includes(r.field)) dependentFields.push(r.field);
                    });
                } else if (enclosingSectionField.visibilityRules && enclosingSectionField.visibilityRules.field) {
                    if (!dependentFields.includes(enclosingSectionField.visibilityRules.field)) {
                        dependentFields.push(enclosingSectionField.visibilityRules.field);
                    }
                }
            }

            if (dependentFields.length === 0) return baseSchema;

            // Yup .when() paths must be relative to the current field's parent object.
            const yupWatchPaths = dependentFields.map(dep => {
                const currentParts = field.name.split('.');
                currentParts.pop(); 
                
                const parentPrefix = `${currentParts.join('.')}.`;
                if (currentParts.length > 0 && dep.startsWith(parentPrefix)) {
                    return dep.slice(parentPrefix.length);
                }
                return dep;
            });

            return baseSchema.when(yupWatchPaths, {
                is: (...values) => {
                    /*
                       Yup's .when() passes values as arguments. 
                       However, since we use checkFieldVisibility which needs the entire formData structure 
                       (to support nested paths like 'address.city'), we reconstruct a partial formData 
                       object from the passed values.
                    */
                    const mockFormDataSnapshot = {};
                    dependentFields.forEach((path, index) => {
                        const parts = path.split('.');
                        let current = mockFormDataSnapshot;
                        for (let i = 0; i < parts.length; i += 1) {
                            const part = parts[i];
                            if (i === parts.length - 1) {
                                current[part] = values[index];
                            } else {
                                current[part] = current[part] || {};
                                current = current[part];
                            }
                        }
                    });

                    if (enclosingSectionField && !checkFieldVisibility(enclosingSectionField, mockFormDataSnapshot)) {
                        return false;
                    }

                    return checkFieldVisibility(field, mockFormDataSnapshot);
                },
                then: (s) => s, // Keep validation if visible
                otherwise: (s) => s.notRequired().nullable().strip(), // Remove validation/value if hidden
            });
        };

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userRole = user.role || 'admin';

        /* 
           Helper to check if a field is hidden via function.
           Note: We only have access to defaultValues/initial state here, 
           so we can't easily check hidden(formData) where formData changes dynamically.
           But for 'organization' which depends on userRole, this works.
        */
        const isFieldHidden = (field) => {
            if (typeof field.hidden === 'function') {
                // We pass empty object or defaultValues as rough approximation
                return field.hidden(defaultValues || {}, userRole, siteSettings);
            }
            return false;
        };

        // Helper to convert plain JSON validation to Yup schema
        const buildYupSchemaFromJson = (field) => {
            const { type, multiple } = field;
            const validation = field.validation || {};

            // Check if validation is already a Yup schema
            const isPredefinedSchema = validation && (
                validation.__isYupSchema__ ||
                (typeof validation.validate === 'function' && typeof validation.test === 'function')
            );

            const isArrayType = type === 'array' || type === 'checkbox-group' || type === 'multiselect' || type === 'grid' || multiple;
            const isNumberType = type === 'number' || type === 'rating';
            const isDateType = type === 'date' || type === 'time' || type === 'multidate';
            const isBooleanType = type === 'checkbox' || type === 'switch';
            const isFileType = type === 'file';
            const isSelectType = type === 'select' || type === 'autocomplete' || type === 'radio';
            const isMobileType = type === 'mobile';
            const isHiddenType = type === 'hidden' || type === 'plan-tree';

            let yupSchema;
            if (isPredefinedSchema) {
                return validation;
            }

            if (isHiddenType) {
                yupSchema = Yup.mixed().nullable();
            } else if (isArrayType) {
                yupSchema = Yup.array().nullable().default([]);
            } else if (isNumberType) {
                yupSchema = Yup.number()
                    .nullable()
                    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value));
            } else if (isDateType) {
                yupSchema = Yup.date()
                    .nullable()
                    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value));
            } else if (isBooleanType) {
                yupSchema = Yup.boolean().nullable();
            } else if (isFileType) {
                yupSchema = Yup.mixed().nullable();

                if (field.accept && field.accept !== '') {
                    yupSchema = yupSchema.test('fileType', `Invalid file type. Allowed: ${field.accept}`, (value) => {
                        if (!value) return true;
                        const files = Array.isArray(value) ? value : [value];
                        const acceptedTypes = field.accept.split(',').map(t => t.trim().toLowerCase());
                        return files.every(file => {
                            if (typeof file === 'string' || !(file instanceof File)) return true;
                            const fileType = file.type ? file.type.toLowerCase() : '';
                            const fileName = file.name ? file.name.toLowerCase() : '';
                            return acceptedTypes.some(accType => {
                                if (accType === 'image/*') return fileType.startsWith('image/');
                                if (accType.startsWith('.')) return fileName.endsWith(accType);
                                return fileType === accType;
                            });
                        });
                    });
                }
            } else if (isSelectType) {
                // Autocomplete/Select can be an object {id, name} or a simple string Value
                yupSchema = Yup.mixed().nullable().transform((value) => {
                    if (typeof value === 'string' && value.trim() === '') return null;
                    if (typeof value === 'object' && value !== null && Object.keys(value).length === 0) return null;
                    return value;
                });
            } else if (!type || ['text', 'textarea', 'string', 'email', 'password', 'mobile'].includes(type)) {
                yupSchema = Yup.string().nullable().transform((value) => (typeof value === 'string' ? value.trim() : value));
            } else {
                yupSchema = Yup.mixed().nullable();
            }

            const isRequired = validation.required || field.required;
            if (isRequired) {
                const requiredMessage = `${field.label || field.name} is required`;
                if (isArrayType) {
                    yupSchema = yupSchema.min(1, requiredMessage);
                } else if (isBooleanType) {
                    yupSchema = yupSchema.oneOf([true], `${field.label || field.name} must be accepted`);
                } else if (isSelectType || isNumberType) {
                    yupSchema = yupSchema.required(requiredMessage);
                    if (isNumberType) {
                        yupSchema = yupSchema.typeError(requiredMessage);
                    }
                } else {
                    yupSchema = yupSchema.required(requiredMessage);
                }
            }

            // Support both min/max and minLength/maxLength
            const minVal = validation.min !== undefined ? validation.min : validation.minLength;
            const maxVal = validation.max !== undefined ? validation.max : validation.maxLength;

            if (!isDateType) {
                if (minVal !== undefined && minVal !== null) {
                    if (yupSchema.min) {
                        yupSchema = yupSchema.min(minVal, isNumberType ? `Minimum is ${minVal}` : `Minimum length is ${minVal}`);
                    }
                }
                if (maxVal !== undefined && maxVal !== null) {
                    if (yupSchema.max) {
                        yupSchema = yupSchema.max(maxVal, isNumberType ? `Maximum is ${maxVal}` : `Maximum length is ${maxVal}`);
                    }
                }
            }

            if (validation.positiveOnly && isNumberType) {
                // Use Math.max to prevent overwriting a higher minimum value
                const effectiveMin = Math.max(Number(minVal || 0), 0);
                yupSchema = yupSchema.min(effectiveMin, `Value must be at least ${effectiveMin}`);
            }

            // Numeric only validation for Mobile/Number fields if they are strings
            if (yupSchema.matches && !isNumberType && (isMobileType || field.name?.toLowerCase().includes('mobile') || field.label?.toLowerCase().includes('mobile'))) {
                yupSchema = yupSchema.matches(/^[0-9]*$/, 'Only numbers are allowed');

                // For dedicated mobile types, also apply length validation if not explicitly overriding
                if (isMobileType && validation.minLength === undefined && validation.maxLength === undefined) {
                    yupSchema = yupSchema.length(10, 'Mobile number must be exactly 10 digits');
                }
            }

            // Date range validation
            if (validation.minDate) {
                const minDate = new Date(validation.minDate);
                if (!Number.isNaN(minDate.getTime())) {
                    // Set hours to 0 to compare dates only
                    minDate.setHours(0, 0, 0, 0);
                    yupSchema = yupSchema.min(minDate, validation.errorMessage || `Date must be on or after ${validation.minDate}`);
                }
            }
            if (validation.maxDate) {
                const maxDate = new Date(validation.maxDate);
                if (!Number.isNaN(maxDate.getTime())) {
                    // Set hours to 23:59:59 to include the whole max date
                    maxDate.setHours(23, 59, 59, 999);
                    yupSchema = yupSchema.max(maxDate, validation.errorMessage || `Date must be on or before ${validation.maxDate}`);
                }
            }

            // Date constraint validation (Future/Past)
            if (validation.dateConstraint && type === 'date') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const offset = Number(validation.dateOffset || 0);
                const limitDate = new Date(today);

                if (validation.dateConstraint === 'future') {
                    limitDate.setDate(limitDate.getDate() + offset);
                    yupSchema = yupSchema.min(limitDate, validation.errorMessage || `Date must be on or after ${limitDate.toISOString().split('T')[0]}`);
                } else if (validation.dateConstraint === 'past') {
                    limitDate.setDate(limitDate.getDate() - offset);
                    limitDate.setHours(23, 59, 59, 999);
                    yupSchema = yupSchema.max(limitDate, validation.errorMessage || `Date must be on or before ${limitDate.toISOString().split('T')[0]}`);
                }
            }

            // Time range validation
            if (type === 'time' && (validation.minTime || validation.maxTime)) {
                yupSchema = yupSchema.test('time-range', 'Time range invalid', function validateTimeRange(value) {
                    if (!value) return true;

                    const dateValue = value instanceof Date ? value : new Date(value);
                    if (Number.isNaN(dateValue.getTime())) return true;

                    const hours = dateValue.getHours();
                    const minutes = dateValue.getMinutes();
                    const totalMinutes = hours * 60 + minutes;

                    if (validation.minTime) {
                        const [minH, minM] = validation.minTime.split(':').map(Number);
                        const minTotal = minH * 60 + minM;
                        if (totalMinutes < minTotal) {
                            return this.createError({ message: validation.errorMessage || `Time must be at or after ${validation.minTime}` });
                        }
                    }

                    if (validation.maxTime) {
                        const [maxH, maxM] = validation.maxTime.split(':').map(Number);
                        const maxTotal = maxH * 60 + maxM;
                        if (totalMinutes > maxTotal) {
                            return this.createError({ message: validation.errorMessage || `Time must be at or before ${validation.maxTime}` });
                        }
                    }

                    return true;
                });
            }

            const regexPattern = validation.pattern || validation.matches;
            if (regexPattern && yupSchema.matches) {
                try {
                    yupSchema = yupSchema.matches(new RegExp(regexPattern), 'Invalid format');
                } catch (e) {
                    console.error("Invalid regex in validation:", regexPattern);
                }
            }

            // Cross-field comparison validation
            if (validation.compareWith && validation.comparisonOperator) {
                const { compareWith, comparisonOperator: operator, errorMessage: customMessage } = validation;

                yupSchema = yupSchema.test('field-comparison', 'Validation failed', function compareFields(value) {
                    // Siblings are in this.parent (the current form values object)
                    const otherValue = this.parent[compareWith];

                    if (value === undefined || value === null || value === '' ||
                        otherValue === undefined || otherValue === null || otherValue === '') {
                        return true;
                    }

                    let isValid = true;
                    let opLabel = '';

                    // Data normalization for comparison
                    let valA = value;
                    let valB = otherValue;

                    if (isNumberType) {
                        valA = Number(value);
                        valB = Number(otherValue);
                    } else if (isDateType) {
                        valA = new Date(value).getTime();
                        valB = new Date(otherValue).getTime();
                    }

                    switch (operator) {
                        case 'lt': isValid = valA < valB; opLabel = 'less than'; break;
                        case 'lte': isValid = valA <= valB; opLabel = 'less than or equal to'; break;
                        case 'gt': isValid = valA > valB; opLabel = 'greater than'; break;
                        case 'gte': isValid = valA >= valB; opLabel = 'greater than or equal to'; break;
                        case 'eq': isValid = valA == valB; opLabel = 'equal to'; break; // eslint-disable-line eqeqeq
                        case 'neq': isValid = valA != valB; opLabel = 'not equal to'; break; // eslint-disable-line eqeqeq
                        default: break;
                    }

                    if (!isValid) {
                        // We try to find a friendly label for the target field if possible
                        // But since we are inside a pure hook, we just use the name or custom message
                        const message = customMessage || `Value must be ${opLabel} ${compareWith}`;
                        return this.createError({ message });
                    }

                    return true;
                });
            }

            // Email validation
            if (validation.email || type === 'email') {
                if (yupSchema.email) {
                    yupSchema = yupSchema.email('Invalid email address');
                }
            }

            return yupSchema;
        };

        const buildNestedSchema = (fieldsList) => {
            const shape = {};

            const addFieldToShape = (name, validation) => {
                const parts = name.split('.');
                let current = shape;

                for (let i = 0; i < parts.length; i += 1) {
                    const part = parts[i];
                    if (i === parts.length - 1) {
                        current[part] = validation;
                    } else {
                        current[part] = current[part] || {};
                        current = current[part];
                    }
                }
            };

            let currentSectionField = null;
            fieldsList.forEach((field) => {
                if (field.type === 'section_break') {
                    currentSectionField = field;
                    return;
                }

                if (field.fields && field.type !== 'array' && field.type !== 'grid') {
                    // Handle sections
                    const secField = field.sectionField || (field.visibilityGroup ? field : null);
                    field.fields.forEach((nestedField) => {
                        if (isFieldHidden(nestedField)) return; // Skip hidden fields

                        let validation = buildYupSchemaFromJson(nestedField);
                        if (validation) {
                            validation = applyVisibilityRules(validation, nestedField, secField);
                            addFieldToShape(nestedField.name, validation);

                            // Add Other field validation if applicable
                            if (Array.isArray(nestedField.options) && nestedField.options.some(o => o.isOther)) {
                                const otherOption = nestedField.options.find(o => o.isOther);
                                const otherFieldName = `${nestedField.name}_other`;

                                const otherValidation = Yup.string().when(nestedField.name, {
                                    is: (val) => {
                                        if (Array.isArray(val)) return val.includes(otherOption.value);
                                        return val === otherOption.value;
                                    },
                                    then: (s) => (nestedField.otherRequired !== false ? s.required('Please provide details') : s),
                                    otherwise: (s) => s.nullable().notRequired()
                                });

                                addFieldToShape(otherFieldName, otherValidation);
                            }
                        }
                    });
                } else {
                    if (isFieldHidden(field)) return; // Skip hidden fields

                    let validation = buildYupSchemaFromJson(field);

                    // Add nested validation for grid/array columns
                    if ((field.type === 'array' || field.type === 'grid') && field.fields) {
                        const itemShape = {};
                        field.fields.forEach(col => {
                            if (col.required) {
                                // Default to required string/mixed, but Yup logic can be improved later
                                itemShape[col.name] = Yup.mixed().required(`${col.label || col.name} is required`);
                            }
                        });

                        // If there are required columns, combine with the array validation
                        if (Object.keys(itemShape).length > 0) {
                            const nestedValidation = Yup.array().of(Yup.object().shape(itemShape)).strict(false);
                            if (validation) {
                                validation = validation.concat(nestedValidation);
                            } else {
                                validation = nestedValidation;
                            }
                        }
                    }

                    if (validation) {
                        validation = applyVisibilityRules(validation, field, currentSectionField);
                        addFieldToShape(field.name, validation);

                        // Add Other field validation if applicable
                        if (Array.isArray(field.options) && field.options.some(o => o.isOther)) {
                            const otherOption = field.options.find(o => o.isOther);
                            const otherFieldName = `${field.name}_other`;

                            const otherValidation = Yup.string().when(field.name, {
                                is: (val) => {
                                    if (Array.isArray(val)) return val.includes(otherOption.value);
                                    return val === otherOption.value;
                                },
                                then: (s) => (field.otherRequired !== false ? s.required('Please provide details') : s),
                                otherwise: (s) => s.nullable().notRequired()
                            });

                            addFieldToShape(otherFieldName, otherValidation);
                        }
                    }
                }
            });

            // Recursive function to convert plain objects to Yup objects
            const convertToYup = (obj) => {
                const yupShape = {};
                Object.keys(obj).forEach(key => {
                    if (obj[key] && (obj[key] instanceof Yup.Schema || obj[key].__isYupSchema__ || typeof obj[key].validate === 'function')) {
                        yupShape[key] = obj[key];
                    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                        yupShape[key] = Yup.object().shape(convertToYup(obj[key]));
                    }
                });
                return yupShape;
            };

            return Yup.object().shape(convertToYup(shape));
        };

        return buildNestedSchema(fields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationSchema, fields, defaultValues, siteSettings]);

    // Build default values if not provided
    const initialValues = useMemo(() => {
        if (defaultValues) return defaultValues;

        const values = {};

        // Helper to process a single field
        const processField = (field) => {
            if (field.type === 'date' || field.type === 'multidate' || field.type === 'time') {
                values[field.name] = field.defaultValue ?? null;
            } else if (field.multiple || field.type === 'checkbox-group' || field.type === 'array' || field.type === 'multiselect' || field.type === 'grid') {
                values[field.name] = field.defaultValue ?? [];
            } else if (field.type === 'switch' || field.type === 'checkbox') {
                values[field.name] = !!field.defaultValue;
            } else if (field.type === 'number' || field.type === 'rating') {
                if (field.defaultValue !== undefined && field.defaultValue !== '') {
                    values[field.name] = Number(field.defaultValue);
                } else {
                    values[field.name] = field.type === 'number' ? '' : 0;
                }
            } else if (field.type === 'color') {
                values[field.name] = field.defaultValue ?? '#1976d2';
            } else {
                values[field.name] = field.defaultValue ?? '';
            }
        };

        fields.forEach((field) => {
            if (field.fields && field.type !== 'array' && field.type !== 'grid') { // Handle layout sections, skip arrays/grids
                field.fields.forEach(processField);
            } else {
                processField(field);
            }
        });

        if (showAvatar) {
            values.avatarUrl = null;
        }

        return values;
    }, [defaultValues, fields, showAvatar]);

    const methods = useForm({
        resolver: (data, context, options) =>
            yupResolver(schema)(data, { ...context, isEdit: !!currentData, siteSettings }, options),
        defaultValues: initialValues,
    });

    const { reset, setValue, watch } = methods;

    // Reset form with currentData when editing and manage isInitializing flag to prevent wiping out dependent fields
    const isInitializing = useRef(true);

    useEffect(() => {
        isInitializing.current = true;
        if (currentData) {
            reset(currentData);
        } else {
            reset(initialValues);
        }

        // Wait for all react-hook-form reset and mounting state cycles to settle before turning off initialization flag
        const timer = setTimeout(() => {
            isInitializing.current = false;
        }, 100);

        return () => clearTimeout(timer);
    }, [currentData, initialValues, reset]);

    useEffect(() => {
        const subscription = watch((value, { name: changedField }) => {
            if (!changedField || isInitializing.current) return;

            // Flatten fields from sections if needed
            let allFields = [];
            fields.forEach((field) => {
                if (field.fields) {
                    allFields = allFields.concat(field.fields);
                } else {
                    allFields.push(field);
                }
            });

            // Find fields that depend on the changed field
            const dependentFields = allFields.filter((f) => {
                if (!f.dependsOn) return false;
                if (Array.isArray(f.dependsOn)) {
                    return f.dependsOn.includes(changedField);
                }
                return f.dependsOn === changedField;
            });

            if (dependentFields.length > 0) {
                dependentFields.forEach((depField) => {
                    if (depField.computeValueAsync && typeof depField.computeValueAsync === 'function') {
                        // Pass current form values to computeValueAsync
                        depField.computeValueAsync(value[changedField], value).then((computedValue) => {
                            setValue(depField.name, computedValue, { shouldValidate: true });
                        }).catch((err) => {
                            console.error('Error in computeValueAsync:', err);
                        });
                    } else if (depField.computeValue && typeof depField.computeValue === 'function') {
                        // Pass current form values to computeValue
                        const computedValue = depField.computeValue(value[changedField], value);
                        setValue(depField.name, computedValue, { shouldValidate: true });
                    } else {
                        const clearValue = depField.multiple ? [] : null;
                        setValue(depField.name, clearValue);
                    }
                });
            }
        });

        return () => subscription.unsubscribe();
    }, [fields, watch, setValue]);

    return methods;
}
